var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import classnames from 'classnames';
import * as React from 'react';
import { classes, st } from './WowImage.st.css';
import Error from 'wix-ui-icons-common/on-stage/Error';
import { Image as WixImage, imageKit, initCustomElement } from '@wix/image';
import { calculateDimensions, resolveFocalPointCoordinates, } from '../Image/ImageUtils';
import { Text } from '../Text';
import { Spinner, SpinnerTypes } from '../Spinner';
import { ImageResizeOptions, ImageAspectRatioPresets, ImageLoadingBehaviorOptions, ImageHoverEffectOptions, } from '../Image/types';
import { extractWixMediaId } from './utils';
var uniqueId = require('lodash/uniqueId');
var STATIC_MEDIA_URL = 'https://static.wixstatic.com/media';
/** Image is a component to literally display an image - whether an absolute with full URL or a media platform item with relative URI */
var WowImage = /** @class */ (function (_super) {
    __extends(WowImage, _super);
    function WowImage(props) {
        var _this = _super.call(this, props) || this;
        _this.state = { isLoaded: false, boundingRectDimensions: null, isError: false };
        _this.containerRef = React.createRef();
        _this.imageRef = React.createRef();
        _this._onLoad = function (event) {
            var onLoad = _this.props.onLoad;
            _this.setState({ isLoaded: true, isError: false });
            onLoad && onLoad(event);
        };
        _this._onError = function (event) {
            var onError = _this.props.onError;
            if (!_this.state.isError) {
                _this.setState({ isError: true });
            }
            onError && onError(event);
        };
        _this._getPlaceholder = function (_a) {
            var fittingType = _a.fittingType, src = _a.src, target = _a.target;
            var placeholder = imageKit.getPlaceholder(fittingType, src, target, {
                autoEncode: true,
            });
            if ((placeholder === null || placeholder === void 0 ? void 0 : placeholder.uri) && !placeholder.uri.startsWith('http')) {
                placeholder.uri = STATIC_MEDIA_URL + "/" + placeholder.uri;
            }
            return placeholder;
        };
        _this._id = uniqueId('new-image');
        _this._containerId = uniqueId('new-image-container');
        initCustomElement();
        return _this;
    }
    WowImage.prototype.componentDidMount = function () {
        var _a;
        var _b = this.props, width = _b.width, height = _b.height, aspectRatio = _b.aspectRatio;
        var imageElement = this.imageRef.current;
        // Updating the state in case it's rendered with SSR and the image is loaded on the client even before registering the `onLoad` event
        if (!this.state.isLoaded && (imageElement === null || imageElement === void 0 ? void 0 : imageElement.complete)) {
            this.setState({ isLoaded: true });
        }
        // Updating the state only if we don't have enough information to calculate the dimensions
        if (!(width && height) && !((width || height) && aspectRatio)) {
            var _c = (_a = this.containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect(), boundingRectWidth = _c.width, boundingRectHeight = _c.height;
            this.setState({
                boundingRectDimensions: {
                    width: boundingRectWidth,
                    height: boundingRectHeight,
                },
            });
        }
    };
    WowImage.prototype.render = function () {
        var _a;
        var _b = this.props, src = _b.src, alt = _b.alt, fluid = _b.fluid, width = _b.width, height = _b.height, resize = _b.resize, onLoad = _b.onLoad, onError = _b.onError, className = _b.className, focalPoint = _b.focalPoint, aspectRatio = _b.aspectRatio, hoverEffect = _b.hoverEffect, sourceWidth = _b.sourceWidth, sourceHeight = _b.sourceHeight, errorMessage = _b.errorMessage, loadingBehavior = _b.loadingBehavior, imageProps = __rest(_b, ["src", "alt", "fluid", "width", "height", "resize", "onLoad", "onError", "className", "focalPoint", "aspectRatio", "hoverEffect", "sourceWidth", "sourceHeight", "errorMessage", "loadingBehavior"]);
        var _c = this.state, isLoaded = _c.isLoaded, boundingRectDimensions = _c.boundingRectDimensions, isError = _c.isError;
        var isBlurLoading = loadingBehavior === ImageLoadingBehaviorOptions.blur;
        var hasLoadingEffect = loadingBehavior && loadingBehavior !== ImageLoadingBehaviorOptions.none;
        var zoomOnHover = hoverEffect === ImageHoverEffectOptions.zoom;
        var aspectRatioAsNumber = ImageAspectRatioPresets[aspectRatio] || aspectRatio;
        var focalPointCoordinates = focalPoint && resolveFocalPointCoordinates(focalPoint);
        // Taking the dimensions from props or from its bounding rectangle in case they're missing
        var targetDimensions = {
            width: (boundingRectDimensions === null || boundingRectDimensions === void 0 ? void 0 : boundingRectDimensions.width) || width,
            height: (boundingRectDimensions === null || boundingRectDimensions === void 0 ? void 0 : boundingRectDimensions.height) || height,
        };
        // Calculating the dimensions considering the given values and aspect ratio
        var calculatedDimensions = calculateDimensions(__assign(__assign({}, targetDimensions), { aspectRatio: aspectRatioAsNumber }));
        var calculatedDimensionsWithHover = calculatedDimensions
            ? {
                width: (calculatedDimensions === null || calculatedDimensions === void 0 ? void 0 : calculatedDimensions.width) * (zoomOnHover ? 1.07 : 1),
                height: (calculatedDimensions === null || calculatedDimensions === void 0 ? void 0 : calculatedDimensions.height) * (zoomOnHover ? 1.07 : 1),
            }
            : null;
        // If full wixstatic url is passed wix media id is extracted to use with wow image
        var imageSrc = extractWixMediaId(src) || src;
        return (React.createElement("div", __assign({ ref: this.containerRef, id: this._containerId, "data-aspect-ratio": aspectRatio, "data-source-width": sourceWidth, "data-source-height": sourceHeight, "data-resize": resize, className: st(classes.root, resize === ImageResizeOptions.cover ? classes.cover : classes.contain, classnames(className, (_a = {},
                _a[classes.fluid] = fluid,
                _a[classes.zoom] = zoomOnHover,
                _a[classes.darken] = hoverEffect === ImageHoverEffectOptions.darken,
                _a[classes.loadSpinner] = loadingBehavior === ImageLoadingBehaviorOptions.spinner,
                _a[classes.loaded] = hasLoadingEffect && isLoaded,
                _a[classes.isError] = isError,
                _a[classes.noImage] = !src,
                _a))) }, (!fluid && {
            style: calculatedDimensions,
        }), { "data-hook": this.props['data-hook'] }),
            src ? (React.createElement(WixImage, __assign({ id: this._id }, imageProps, { containerId: this._containerId, uri: imageSrc, name: imageSrc, width: sourceWidth, height: sourceHeight, displayMode: resize === 'cover' ? 'fill' : 'fit', alt: alt, focalPoint: focalPointCoordinates, containerWidth: calculatedDimensionsWithHover === null || calculatedDimensionsWithHover === void 0 ? void 0 : calculatedDimensionsWithHover.width, containerHeight: calculatedDimensionsWithHover === null || calculatedDimensionsWithHover === void 0 ? void 0 : calculatedDimensionsWithHover.height }, (zoomOnHover && {
                targetWidth: calculatedDimensionsWithHover === null || calculatedDimensionsWithHover === void 0 ? void 0 : calculatedDimensionsWithHover.width,
                targetHeight: calculatedDimensionsWithHover === null || calculatedDimensionsWithHover === void 0 ? void 0 : calculatedDimensionsWithHover.height,
            }), { className: classes.image, onError: this._onError, onLoad: this._onLoad, getPlaceholder: isBlurLoading ? this._getPlaceholder : undefined, placeholderTransition: isBlurLoading ? 'blur' : null }))) : null,
            isError && (React.createElement("div", { className: classes.errorWrapper },
                React.createElement(Error, { className: classes.errorIcon }),
                React.createElement(Text, { className: classes.errorMessage },
                    React.createElement("span", { className: classes.srError }, alt),
                    errorMessage))),
            loadingBehavior === ImageLoadingBehaviorOptions.spinner ? (React.createElement("div", { className: classes.spinnerOverlay, "aria-hidden": "true" },
                React.createElement(Spinner, { isCentered: true, diameter: 24, type: SpinnerTypes.regular, className: classes.spinner }))) : null));
    };
    WowImage.displayName = 'Image';
    WowImage.defaultProps = {
        resize: ImageResizeOptions.contain,
        errorMessage: 'Image not found',
    };
    return WowImage;
}(React.Component));
export { WowImage };
//# sourceMappingURL=WowImage.js.map